import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Permission, PermissionsResponse } from '../models/permission.model';
import { API_ENDPOINTS } from '../constants/api.constants';
import { environment } from '../../../environments/environment';
import { LoggingService } from './logging.service';

/**
 * Permissions Service
 * Manages user permissions for role-based access control
 * Based on temp/app/core/service/auth.service.ts permission logic
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private permissionsSignal = signal<Permission[]>([]);
  
  // Public readonly access to permissions
  readonly permissions = this.permissionsSignal.asReadonly();
  
  // Computed signal for quick permission checks
  readonly permissionIds = computed(() => 
    this.permissionsSignal().map(p => p._id)
  );

  constructor(
    private http: HttpClient,
    private logger: LoggingService
  ) {}

  /**
   * Load permissions for a user
   * Should be called after successful login
   */
  async loadPermissions(userId: string): Promise<void> {
    // Don't reload if already loaded
    if (this.permissionsSignal().length > 0) {
      this.logger.info('Permissions already loaded');
      return;
    }

    try {
      const response = await fetch(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.PERMISSIONS(userId)}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load permissions');
      }

      const data: PermissionsResponse = await response.json();
      
      if (data.success && data.data) {
        this.permissionsSignal.set(data.data);
        this.logger.info('Permissions loaded', { count: data.data.length });
      }
    } catch (error) {
      this.logger.error('Failed to load permissions', error);
      // Set empty array on error
      this.permissionsSignal.set([]);
    }
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permissionId: string): boolean {
    return this.permissionIds().includes(permissionId);
  }

  /**
   * Check if user has ANY of the specified permissions
   */
  hasAnyPermission(permissionIds: string[]): boolean {
    if (!permissionIds || permissionIds.length === 0) {
      return true; // No permissions required
    }
    
    const userPermissions = this.permissionIds();
    return permissionIds.some(id => userPermissions.includes(id));
  }

  /**
   * Check if user has ALL of the specified permissions
   */
  hasAllPermissions(permissionIds: string[]): boolean {
    if (!permissionIds || permissionIds.length === 0) {
      return true; // No permissions required
    }
    
    const userPermissions = this.permissionIds();
    return permissionIds.every(id => userPermissions.includes(id));
  }

  /**
   * Clear all permissions (on logout)
   */
  clearPermissions(): void {
    this.permissionsSignal.set([]);
    this.logger.info('Permissions cleared');
  }

  /**
   * Get all available permissions from backend
   * Used for roles management UI
   */
  getAllPermissions(): Observable<Permission[]> {
    return this.http.get<PermissionsResponse>(
      `${environment.apiUrl}/auth/permissions`
    ).pipe(
      map((response) => {
        if (!response.success || !response.data) {
          throw new Error(response.message || 'Failed to load permissions');
        }
        return response.data;
      })
    );
  }

  /**
   * Create a new permission
   * @param permission Permission data (_id and description)
   */
  createPermission(permission: { _id: string; description: string | null }): Observable<Permission> {
    return this.http.post<PermissionsResponse>(
      `${environment.apiUrl}/auth/permissions`,
      permission
    ).pipe(
      map((response) => {
        if (!response.success || !response.data) {
          throw new Error(response.message || 'Failed to create permission');
        }
        this.logger.info('Permission created', { id: permission._id });
        // Return the first item if data is an array, otherwise return data
        return Array.isArray(response.data) ? response.data[0] : response.data as any;
      })
    );
  }

  /**
   * Update an existing permission
   * @param permissionId Permission ID to update
   * @param updates Fields to update (typically just description)
   */
  updatePermission(permissionId: string, updates: { description?: string | null }): Observable<Permission> {
    return this.http.put<PermissionsResponse>(
      `${environment.apiUrl}/auth/permission`,
      {
        _id: permissionId,
        ...updates
      }
    ).pipe(
      map((response) => {
        if (!response.success || !response.data) {
          throw new Error(response.message || 'Failed to update permission');
        }
        this.logger.info('Permission updated', { id: permissionId });
        // Return the first item if data is an array, otherwise return data
        return Array.isArray(response.data) ? response.data[0] : response.data as any;
      })
    );
  }

  /**
   * Delete a permission
   * @param permissionId Permission ID to delete
   */
  deletePermission(permissionId: string): Observable<void> {
    return this.http.delete<PermissionsResponse>(
      `${environment.apiUrl}/auth/permissions/${permissionId}`
    ).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to delete permission');
        }
        this.logger.info('Permission deleted', { id: permissionId });
      })
    );
  }
}

